// Store metadata/defaults are shared reads; selections and operations belong to one order.
export function createWarehousePreferenceState({ request, getCompanyID, changed }) {
    const entries = new Map(), choices = new Map();
    let companyID = getCompanyID(), disposed = false;
    const sync = () => { if (companyID !== getCompanyID()) { companyID = getCompanyID(); entries.clear(); choices.clear(); } };
    const storeKey = row => { sync(); return JSON.stringify([companyID, row?.retailBrandID, row?.storeID]); };
    const orderKey = row => JSON.stringify([storeKey(row), row?._id]);
    const usable = row => !!companyID && !!row?._id && !!row?.retailBrandID && !!row?.storeID &&
        row?.retailBrandPosStatus === "Connected" && row?.storePosStatus === "Connected";
    const current = entry => { sync(); return !disposed && entries.get(entry.key) === entry; };
    const publish = entry => { if (current(entry)) changed(); };
    function entryFor(row) {
        const key = storeKey(row), binding = JSON.stringify([row?.retailBrandPosStatus, row?.storePosStatus, row?.retailBrandPosID, row?.storePosID]);
        let entry = entries.get(key);
        if (!entry || entry.binding !== binding) {
            entry = { key, binding, companyID, retailBrandID: row?.retailBrandID, storeID: row?.storeID,
                status: "idle", warehouses: [], preference: null, revision: 0 };
            entries.set(key, entry);
        }
        return entry;
    }
    function apply(entry, data) {
        if (!data || typeof data.scopeKey !== "string" || !Number.isSafeInteger(data.revision) || data.revision < 0 ||
            !Array.isArray(data.warehouses) || data.warehouses.some(w => !["string", "number"].includes(typeof w?.id) || !String(w.id) || typeof w.name !== "string") ||
            new Set(data.warehouses.map(w => String(w.id))).size !== data.warehouses.length) throw Error("Invalid destination response");
        // A late same-scope CAS response must not regress newer metadata.
        if (entry.scopeKey === data.scopeKey && data.revision < entry.revision) return;
        Object.assign(entry, { scopeKey: data.scopeKey, revision: data.revision, warehouses: data.warehouses,
            preference: data.preference && data.warehouses.some(w => String(w.id) === data.preference.warehouseID) ? data.preference : null,
            status: "ready", error: "" });
    }
    const selectors = entry => ({ companyID: entry.companyID, retailBrandID: entry.retailBrandID, storeID: entry.storeID });
    async function load(row, force = false) {
        const entry = entryFor(row);
        if (disposed || !usable(row)) return;
        if (entry.promise) return entry.promise;
        if (!force && entry.status !== "idle") return entry;
        if (entry.status !== "ready") entry.status = "loading";
        publish(entry);
        entry.promise = (async () => {
            try {
                const res = await request("getDestinationWarehousePreference", selectors(entry));
                if (!current(entry)) return;
                if (res?.data?.status !== 200) throw Error("Destination unavailable");
                apply(entry, res.data.data);
            } catch { if (current(entry)) { entry.status = "error"; entry.error = "Warehouses unavailable. Retry to reconcile."; } }
            finally { entry.promise = null; publish(entry); }
            return current(entry) && entry.status === "ready" ? entry : undefined;
        })();
        return entry.promise;
    }
    function choiceFor(row, entry) {
        const key = orderKey(row);
        let choice = choices.get(key);
        if (!choice || choice.binding !== entry.binding || choice.scopeKey && entry.scopeKey && choice.scopeKey !== entry.scopeKey) {
            choice = { binding: entry.binding, initialized: false, value: "" };
            choices.set(key, choice);
        }
        if (!choice.initialized && entry.status === "ready") {
            choice.value = entry.preference?.warehouseID || "";
            choice.scopeKey = entry.scopeKey;
            choice.initialized = true;
        }
        return choice;
    }
    function select(row, value) {
        const entry = entryFor(row), choice = choiceFor(row, entry);
        if (!usable(row) || entry.status !== "ready" || !entry.warehouses.some(w => String(w.id) === String(value))) return;
        choice.value = String(value); choice.initialized = true; choice.scopeKey = entry.scopeKey;
        publish(entry);
    }
    function state(row) {
        const entry = entryFor(row), choice = choiceFor(row, entry);
        if (!usable(row)) return { orderKey: orderKey(row), disabled: true, ready: false, warehouses: [], value: "" };
        const selected = entry.warehouses.find(w => String(w.id) === choice.value);
        return { orderKey: orderKey(row), warehouses: entry.warehouses, value: selected ? String(selected.id) : "", warehouse: selected,
            scopeKey: entry.scopeKey, revision: entry.revision, ready: entry.status === "ready" && !!selected,
            loading: entry.status === "loading", disabled: entry.status !== "ready", error: entry.error,
            onChange: value => select(row, value), onRetry: () => load(row, true) };
    }
    // Only the caller's authoritative completed EXPORT may invoke this method.
    // The captured scope/revision is immutable; neither conflicts nor uncertain writes are retried.
    async function saveAfterExport(row, selection) {
        const entry = entryFor(row);
        if (!current(entry) || entry.companyID !== selection.companyID || entry.scopeKey !== selection.scopeKey ||
            !entry.warehouses.some(w => String(w.id) === String(selection.warehouseID))) return { saved: false, outcome: "context-changed" };
        try {
            const res = await request("saveDestinationWarehousePreference", { ...selectors(entry), warehouseID: String(selection.warehouseID),
                expectedScopeKey: selection.scopeKey, expectedRevision: selection.revision });
            if (res?.data?.status === 200) {
                if (current(entry)) { apply(entry, res.data.data); publish(entry); }
                return { saved: true };
            }
            return { saved: false, outcome: res?.data?.status === 409 ? "conflict" :
                res?.data?.data?.writeOutcome === "not-written" ? "not-written" : "unknown" };
        } catch { return { saved: false, outcome: "unknown" }; }
    }
    return { load, select, state, saveAfterExport, dispose: () => { disposed = true; entries.clear(); choices.clear(); } };
}
